import LogoNav from "../assets/logo_nav.webp";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {HashLink} from "react-router-hash-link";

const sidebarVariants = {
    open: (height = 1000) => ({
        clipPath: `circle(${height * 2 + 200}px at calc(100% - 40px) 40px)`,
        transition: {
            type: "spring",
            stiffness: 20,
            restDelta: 2,
        },
    }),
    closed: {
        clipPath: "circle(0px at calc(100% - 40px) 40px)",
        transition: {
            delay: 0.2,
            type: "spring",
            stiffness: 400,
            damping: 40,
        },
    },
};


const Path = (props) => (
    <motion.path
        fill="transparent"
        strokeWidth="3"
        stroke="white"
        strokeLinecap="round"
        {...props}
    />
);

const MenuToggle = ({ toggle }) => (
    <button style={toggleContainer} onClick={toggle}>
        <svg width="23" height="23" viewBox="0 0 23 23">
            <Path
                variants={{
                    closed: { d: "M 2 2.5 L 20 2.5" },
                    open: { d: "M 3 16.5 L 17 2.5" },
                }}
            />
            <Path
                d="M 2 9.423 L 20 9.423"
                variants={{
                    closed: { opacity: 1 },
                    open: { opacity: 0 },
                }}
                transition={{ duration: 0.1 }}
            />
            <Path
                variants={{
                    closed: { d: "M 2 16.346 L 20 16.346" },
                    open: { d: "M 3 2.5 L 17 16.346" },
                }}
            />
        </svg>
    </button>
);

const toggleContainer = {
    position: "absolute",
    top: 20,
    right: 20,
    zIndex: 60,
    background: "transparent",
    border: "none",
    cursor: "pointer",
};

const useDimensions = (ref) => {
    const dimensions = useRef({ width: 0, height: 0 });

    useEffect(() => {
        if (ref.current) {
            dimensions.current.width = ref.current.offsetWidth;
            dimensions.current.height = ref.current.offsetHeight;
        }
    }, [ref]);

    return dimensions.current;
};

const MotionHashLink = motion.create(HashLink);

function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const { height } = useDimensions(containerRef);
    const [isProjectsOpen, setIsProjectsOpen] = useState(false);
    const [isProjectsMobileOpen, setIsProjectsMobileOpen] = useState(false);

    useEffect(() => {
        document.body.classList.toggle("overflow-hidden", isOpen);
        return () => document.body.classList.remove("overflow-hidden");
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest(".projects-toggle")) return;
            if (isProjectsOpen && !event.target.closest(".projects-dropdown-desktop")) {
                setIsProjectsOpen(false);
            }
            if (isProjectsMobileOpen && !event.target.closest(".projects-dropdown-mobile")) {
                setIsProjectsMobileOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isProjectsOpen, isProjectsMobileOpen]);


    return (
        <nav id="navbar" className="fixed top-0 left-0 bg-black bg-opacity-40 border-white text-light px-6 py-4 flex items-center justify-between w-full z-50">
            <div className="flex items-center w-2/3 md:w-1/3">
                <HashLink to="/#hero" smooth>
                    <img src={LogoNav} alt="4AI logo" className="h-8 sm:h-10 lg:h-14" />
                </HashLink>
            </div>
            <div className="hidden md:flex space-x-12 font-light text-base lg:text-lg xl:text-xl w-1/3 md:w-2/3 justify-end">
                    <HashLink to="/#hero" smooth className="hover:underline">Home</HashLink>
                    <div className="relative">
                        <button
                            onClick={() => setIsProjectsOpen((prev) => !prev)}
                            className="hover:underline flex items-center space-x-1 projects-toggle"
                        >
                            <span>Prodotti</span>
                            <svg
                                className={`size-[2vw] transform transition-transform ${isProjectsOpen ? "rotate-180" : ""} lg:size-[1.8vw] xl:lg:size-[1.2vw]`}
                                fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {isProjectsOpen && (
                            <div className="absolute top-full left-0 mt-2 w-48 bg-black text-light rounded-md shadow-md z-50 projects-dropdown-desktop">
                                <HashLink
                                    to="/#two-sequel"
                                    smooth
                                    onClick={() => setIsProjectsOpen(false)}
                                    className="block px-4 py-2 hover:underline"
                                >
                                    2Sequel
                                </HashLink>
                                <HashLink
                                    to="/#ailights"
                                    smooth
                                    onClick={() => setIsProjectsOpen(false)}
                                    className="block px-4 py-2 hover:underline"
                                >
                                    AiLights
                                </HashLink>
                                {/*<HashLink*/}
                                {/*    to="/#report"*/}
                                {/*    smooth*/}
                                {/*    onClick={() => setIsProjectsOpen(false)}*/}
                                {/*    className="block px-4 py-2 hover:underline"*/}
                                {/*>*/}
                                {/*    Report Sostenibilità*/}
                                {/*</HashLink>*/}
                                <HashLink
                                    to="/#traid"
                                    smooth
                                    onClick={() => setIsProjectsOpen(false)}
                                    className="block px-4 py-2 hover:underline"
                                >
                                    Traid
                                </HashLink>
                            </div>
                        )}
                    </div>
                    <HashLink to="/#team" smooth className="hover:underline">Chi Siamo</HashLink>
                    <HashLink to="/#contacts" smooth className="hover:underline">Contatti</HashLink>
            </div>
            <div className="md:hidden" ref={containerRef}>
                <motion.nav
                    initial={false}
                    animate={isOpen ? "open" : "closed"}
                    custom={height}
                >
                    <motion.div
                        className="fixed inset-0 bg-black z-40"
                        variants={sidebarVariants}
                        initial={false}
                        animate={isOpen ? "open" : "closed"}
                    />
                    <MenuToggle toggle={() => setIsOpen(!isOpen)} />
                    {isOpen && (
                        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center space-y-8 text-2xl text-white">
                            <MotionHashLink
                                to="/#hero"
                                smooth
                                onClick={() => setIsOpen(false)}
                                className="hover:underline"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                            >
                                Home
                            </MotionHashLink>
                            <div className="flex flex-col items-center">
                                <motion.button
                                    onClick={() => setIsProjectsMobileOpen(prev => !prev)}
                                    className="hover:underline flex items-center space-x-2 projects-toggle"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <span>Prodotti</span>
                                    <svg
                                        className={`w-5 h-5 transition-transform ${isProjectsMobileOpen ? "rotate-180" : ""}`}
                                        fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </motion.button>
                                <AnimatePresence>
                                    {isProjectsMobileOpen && (
                                        <motion.div
                                            key="mobile-submenu"
                                            className="flex flex-col items-center mt-2 space-y-2 text-base projects-dropdown-mobile"
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <MotionHashLink
                                                to="/#two-sequel"
                                                smooth
                                                onClick={() => {
                                                    setIsProjectsMobileOpen(false);
                                                    setIsOpen(false);
                                                }}
                                                className="hover:underline"
                                            >
                                                2Sequel
                                            </MotionHashLink>
                                            <MotionHashLink
                                                to="/#ailights"
                                                smooth
                                                onClick={() => {
                                                    setIsProjectsMobileOpen(false);
                                                    setIsOpen(false);
                                                }}
                                                className="hover:underline"
                                            >
                                                AiLights
                                            </MotionHashLink>
                                            {/*<MotionHashLink*/}
                                            {/*    to="/#report"*/}
                                            {/*    smooth*/}
                                            {/*    onClick={() => {*/}
                                            {/*        setIsProjectsMobileOpen(false);*/}
                                            {/*        setIsOpen(false);*/}
                                            {/*    }}*/}
                                            {/*    className="hover:underline"*/}
                                            {/*>*/}
                                            {/*    Report Sostenibilità*/}
                                            {/*</MotionHashLink>*/}
                                            <MotionHashLink
                                                to="/#traid"
                                                smooth
                                                onClick={() => {
                                                    setIsProjectsMobileOpen(false);
                                                    setIsOpen(false);
                                                }}
                                                className="hover:underline"
                                            >
                                                Traid
                                            </MotionHashLink>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            <MotionHashLink
                                to="/#team"
                                smooth
                                onClick={() => setIsOpen(false)}
                                className="hover:underline"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                Chi Siamo
                            </MotionHashLink>
                            <MotionHashLink
                                to="/#contacts"
                                smooth
                                onClick={() => setIsOpen(false)}
                                className="hover:underline"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                            >
                                Contatti
                            </MotionHashLink>
                        </div>
                    )}
                </motion.nav>
            </div>
        </nav>
    );
}

export default Navbar;
